import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BonusReport,
  SendBonusReportMode,
  TimesheetSubmissionTiming,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

export interface BonusSendResult {
  recipients: number;
  memberCount: number;
}

export interface SendBonusArgs {
  recipients: string[];
  mode: SendBonusReportMode;
}

export function useBonusReport(): UseQueryResult<BonusReport> {
  return useQuery({
    queryKey: ['reports', 'bonus'] as const,
    queryFn: () => api.get<BonusReport>('/reports/bonus'),
    staleTime: 60_000,
  });
}

export function useSendBonusReport(): UseMutationResult<BonusSendResult, unknown, SendBonusArgs> {
  return useMutation({
    mutationFn: ({ recipients, mode }: SendBonusArgs) =>
      api.post<BonusSendResult>('/reports/bonus/send', { recipients, mode }),
  });
}

export interface SetTimingArgs {
  timesheetId: string;
  timing: TimesheetSubmissionTiming | null;
}

/** Sets a timesheet's manual on-time/late override, then refreshes the bonus report. */
export function useSetTimesheetTiming(): UseMutationResult<unknown, unknown, SetTimingArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ timesheetId, timing }: SetTimingArgs) =>
      api.patch(`/timesheets/${timesheetId}/timing`, { timing }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'bonus'] });
    },
  });
}

export interface SetBonusDisqualificationArgs {
  timesheetId: string;
  disqualified: boolean;
  note?: string | null;
}

/** Sets a timesheet's bonus-disqualified flag/note, then refreshes the bonus report. */
export function useSetBonusDisqualification(): UseMutationResult<
  unknown,
  unknown,
  SetBonusDisqualificationArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ timesheetId, disqualified, note }: SetBonusDisqualificationArgs) =>
      api.patch(`/timesheets/${timesheetId}/bonus-disqualification`, { disqualified, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'bonus'] });
    },
  });
}

